/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { DrawRuleRestService } from '@gh/builder-shared-data';
import { getRouteParam, getRouteQueryParam, InitViewService, isRouteForCreationMode } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { GridLayoutService } from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  DrawRuleDetails,
  drawRuleDetailsForClone,
  drawRuleDetailsFromDto,
  drawRuleDetailsToDto, DrawRuleStage,
} from './draw-rule-details.model';

@Injectable()
export class DrawRuleDetailsBusinessService implements InitViewService {

  constructor(private drawRuleRest: DrawRuleRestService,
              private gridLayouts: GridLayoutService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    const drawRuleId = getRouteParam(route, 'drawRuleId');
    const sourceDrawRuleId = getRouteQueryParam(route, 'cloneFrom');
    const isNew = isRouteForCreationMode(route);
    const isClone = isNew && isNotNil(sourceDrawRuleId);

    return {
      details: isNew
        ? (isClone ? this.clone(sourceDrawRuleId) : this.getInit())
        : this.get(drawRuleId),
      drawRuleCpStageLayout: this.gridLayouts.get('drawRuleCpStages'),
      drawRuleCcStageLayout: this.gridLayouts.get('drawRuleCcStages'),
    };
  }

  getInit(): Observable<DrawRuleDetails> {
    return this.drawRuleRest.getInit().pipe(map$(drawRuleDetailsFromDto));
  }

  get(id: ID): Observable<DrawRuleDetails> {
    return this.drawRuleRest.get(id).pipe(map$(drawRuleDetailsFromDto));
  }

  clone(id: ID): Observable<DrawRuleDetails> {
    return this.drawRuleRest.clone(id).pipe(map$(drawRuleDetailsFromDto), map$(drawRuleDetailsForClone));
  }

  save(details: DrawRuleDetails): Observable<DrawRuleDetails> {
    return details.drawRuleId ? this.update(details) : this.create(details);
  }

  create(details: DrawRuleDetails): Observable<DrawRuleDetails> {
    return this.drawRuleRest.create(drawRuleDetailsToDto(details)).pipe(map$(drawRuleDetailsFromDto));
  }

  update(details: DrawRuleDetails): Observable<DrawRuleDetails> {
    return this.drawRuleRest.update(drawRuleDetailsToDto(details)).pipe(map$(drawRuleDetailsFromDto));
  }

  checkStageUsage(ruleId: ID, drawRuleStage: DrawRuleStage): Observable<any> {
    return this.drawRuleRest.checkStageUsage(ruleId, drawRuleStage);
  }
}
